import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import memoize from "memoize-one";
import isEqual from "lodash/isEqual";
import { Button, Radio, Icon, Tooltip } from "antd";
import PaperPlane from "react-icons/lib/io/ios-paperplane";
import ReactQuill from "react-quill";

import { getAgentCannedResponses } from "../../../../../../data/redux/member_details/actions"

import { INPUT_COMPOSER_TYPE, METHOD_TYPES, TEXT_TYPE } from "../../../../../../data/config/constants";
import { MODULE_CONFIG } from "../../../../../../data/config/urls"

import {
  filterArrayBySearchText,
  createStringFromTemplate,
  removeStringLastCharacter,
  debounced
} from "../../../../../../data/config/utils";

import "./index.scss";
import "react-quill/dist/quill.core.css"

import ShortcutList from "./shortcutlist";
import SpinnerLoader from "../../../../../../components/spinnerloader";

class ChatComposer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.typingTimer = null;
    this.editorModules = this.getEditorModules()
    this.editorRef = React.createRef();
    this.popupOverlayRef = React.createRef();
    this.debouncedFilterCannedGroupBySearchKey = debounced(this.filterCannedGroupBySearchKey, 150);
    this.state = {
      editorHtml: "",
      typing: false,
      text_type: TEXT_TYPE.USER_TYPED,
      cnId: "",
      composer_type: INPUT_COMPOSER_TYPE.BARGE,
      show_tags_popup: false,
      filtered_tags: props.tags,
      show_canned_responses: false,
      selected_node: {},
      default_expand_all: false,
      filtered_cn_group: this.changeCannedResponsesInGroup(props.canned_responses)
    };
  }

  componentDidMount() {
    this.focusEditor()
  }

  componentDidUpdate(prevProps) {
    const { canned_responses, tags, active_chat_item } = this.props;
    if (prevProps.canned_responses.length === 0 && canned_responses.length !== 0) {
      let cn_search_key = ""
      const plainText = removeStringLastCharacter(this.getEditorText(), "\n")
      if (plainText && plainText.charAt(0) === "#")
        cn_search_key = plainText.slice(1, plainText.length);
      this.debouncedFilterCannedGroupBySearchKey(cn_search_key.trim(), canned_responses);
    }
    if (!isEqual(prevProps.tags, tags) || prevProps.tags.length !== tags.length) {
      let tag_search_key = ""
      const plainText = removeStringLastCharacter(this.getEditorText(), "\n")
      if (plainText && plainText.charAt(0) === "@")
        tag_search_key = plainText.slice(1, plainText.length)
      this.setState({
        filtered_tags: tag_search_key ? tags : filterArrayBySearchText(tag_search_key, tags, "name"),
      });
    }
    if (active_chat_item.psid !== prevProps.active_chat_item.psid) {
      this.setState({
        editorHtml: "",
        text_type: TEXT_TYPE.USER_TYPED
      }, this.focusEditor);
    }
  }

  focusEditor = () => {
    if (this.editorRef && this.editorRef.current)
      this.editorRef.current.focus();
  }

  getEditorText = () => {
    let text = ""
    if (this.editorRef && this.editorRef.current) {
      const editor = this.editorRef.current.getEditor()
      if (editor)
        text = editor.getText().trim()
    }
    return text
  }

  getEditorModules = () => {
    const modules = {
      toolbar: { container: "#toolbar" },
      keyboard: {
        bindings: {
          custom: {
            key: 'enter',
            shiftKey: false,
            handler: this.sendHtmlContent
          }
        }
      },
      clipboard: {
        matchVisual: false,
        ...(MODULE_CONFIG.CHATS.EDITOR.STRIP_PASTED_STYLES && {
          matchers: [
            [Node.ELEMENT_NODE, (node, delta) => {
              let ops = []
              delta.ops.forEach(op => {
                if (op.insert && typeof op.insert === 'string') {
                  ops.push({
                    insert: op.insert
                  })
                }
              })
              delta.ops = ops
              return delta
            }]
          ]
        })
      }
    }
    return modules
  }

  onClickOutsidePopup = event => {
    if (this.popupOverlayRef && this.popupOverlayRef.current && this.popupOverlayRef.current.contains(event.target)) {
      return;
    }
    this.closeOverlayPopup();
  };

  showCannedResponses = () => {
    const { canned_responses, actions, admin_email } = this.props
    document.addEventListener("click", this.onClickOutsidePopup, false);
    if (canned_responses.length === 0 && admin_email)
      actions.getAgentCannedResponses({ email: admin_email })
    this.setState({ show_canned_responses: true });
  };

  closeOverlayPopup = () => {
    document.removeEventListener("click", this.onClickOutsidePopup, false);
    this.setState({
      show_tags_popup: false,
      show_canned_responses: false,
      selected_node: {},
      default_expand_all: false,
      filtered_cn_group: this.changeCannedResponsesInGroup(this.props.canned_responses)
    });
  };

  changeCannedResponsesInGroup = memoize(canned_responses => {
    let cn_group = {};
    canned_responses.forEach(cn_res => {
      if (cn_res.heading && cn_res.heading.trim().length > 0) {
        if (cn_group[cn_res.heading] === undefined)
          cn_group[cn_res.heading] = {};
        if (cn_res.subHeading && cn_res.subHeading.trim().length > 0) {
          if (cn_group[cn_res.heading][cn_res.subHeading] === undefined)
            cn_group[cn_res.heading][cn_res.subHeading] = {};
          cn_group[cn_res.heading][cn_res.subHeading][cn_res.shortcut] = {
            id: cn_res._id,
            messages: cn_res.messages
          };
        } else {
          cn_group[cn_res.heading][cn_res.shortcut] = {
            id: cn_res._id,
            messages: cn_res.messages
          };
        }
      } else {
        cn_group[cn_res.shortcut] = {
          id: cn_res._id,
          messages: cn_res.messages
        };
      }
    });
    return cn_group;
  }, isEqual);

  onSelectTreeNode = (key, event) => {
    if (key && event && event.node && event.node.props && event.node.props.value) {
      this.setState({
        selected_node: {
          key: key[0],
          messages: event.selected ? event.node.props.value.messages : null,
          id: event.selected ? event.node.props.value.id : ""
        }
      });
    }
  };

  filterCannedResponsesBySearchText = (text, array, key) => {
    return array.filter(item => {
      return (item[key] && item[key] !== undefined && item[key].toLowerCase().indexOf(text.toLowerCase()) === 0);
    });
  };

  filterCannedGroupBySearchKey = (search_key, canned_responses, callback) => {
    const filtered_canned_responses = this.filterCannedResponsesBySearchText(search_key, canned_responses, "shortcut");
    this.setState({
      default_expand_all: true,
      filtered_cn_group: this.changeCannedResponsesInGroup(filtered_canned_responses),
    }, callback);
  };

  onClickCannedMessage = cn => {
    const { active_chat_item } = this.props;
    const selected_cn = active_chat_item ? createStringFromTemplate(cn.message, active_chat_item) : cn.message;
    const editor = this.editorRef.current.getEditor()
    editor.setText(selected_cn)
    editor.setSelection(editor.getLength())
    this.setState({
      text_type: TEXT_TYPE.CANNED_RESPONSE,
      cnId: cn.id
    }, () => {
      this.focusEditor()
      this.closeOverlayPopup()
    })
  };

  onChangeComposerType = event => {
    this.setState({ composer_type: event.target.value });
  };

  showTagsPopup = () => {
    document.addEventListener("click", this.onClickOutsidePopup, false);
    this.setState({ show_tags_popup: true });
  };

  onSelectTag = tag => {
    const { updateSelectedTag, active_chat_item } = this.props;
    this.closeOverlayPopup();
    const plainText = this.getEditorText()
    if (plainText && plainText.charAt(0) === "@")
      this.setState({ editorHtml: "" }, this.focusEditor)
    if (updateSelectedTag && active_chat_item) {
      const payload = {
        clientPsid: active_chat_item.psid,
        tag,
      };
      updateSelectedTag(payload, METHOD_TYPES.PUT);
    }
  };

  getFirstFilterCannedMessage = key => {
    const { filtered_cn_group } = this.state;
    const cn_keys1 = Object.keys(filtered_cn_group);
    for (var i = 0; i < cn_keys1.length; i++) {
      if (key === cn_keys1[i] && filtered_cn_group[cn_keys1[i]].messages) {
        return ({
          message: filtered_cn_group[cn_keys1[i]].messages[0],
          id: filtered_cn_group[cn_keys1[i]].id
        });
      } else {
        const cn_keys2 = Object.keys(filtered_cn_group[cn_keys1[i]]);
        for (var j = 0; j < cn_keys2.length; j++) {
          if (key === cn_keys2[j] && filtered_cn_group[cn_keys1[i]][cn_keys2[j]].messages) {
            return ({
              message: filtered_cn_group[cn_keys1[i]][cn_keys2[j]].messages[0],
              id: filtered_cn_group[cn_keys1[i]][cn_keys2[j]].id
            });
          } else {
            const cn_keys3 = Object.keys(filtered_cn_group[cn_keys1[i]][cn_keys2[j]]);
            for (var k = 0; k < cn_keys3.length; k++) {
              if (key === cn_keys3[k] && filtered_cn_group[cn_keys1[i]][cn_keys2[j]][cn_keys3[k]].messages) {
                return ({
                  message: filtered_cn_group[cn_keys1[i]][cn_keys2[j]][cn_keys3[k]].messages[0],
                  id: filtered_cn_group[cn_keys1[i]][cn_keys2[j]][cn_keys3[k]].id
                });
              }
            }
          }
        }
      }
    }
    return null;
  };

  stopTypingIndicator = () => {
    this.setState({ typing: false }, () => {
      const { sendTypingIndication } = this.props;
      if (sendTypingIndication)
        sendTypingIndication(false);
    });
  };

  sendHtmlContent = () => {
    const { handleMessageSend } = this.props;
    const { editorHtml, composer_type, text_type, cnId } = this.state;
    const text = this.getEditorText()
    if (text) {
      handleMessageSend(editorHtml, composer_type, text_type, cnId);
      this.setState({
        editorHtml: "",
        text_type: TEXT_TYPE.USER_TYPED,
        cnId: ""
      }, this.focusEditor)
    }
    this.stopTypingIndicator();
    if (this.typingTimer)
      clearTimeout(this.typingTimer);
  }

  onChangeEditor = (editorHtml, delta, source, editor) => {
    this.setState({ editorHtml })
    const plainText = removeStringLastCharacter(editor.getText(), "\n")
    if (plainText === "#")
      this.showCannedResponses();
    else if (plainText === "@")
      this.showTagsPopup();
    else if (plainText === "")
      this.closeOverlayPopup();
    if (plainText.charAt(0) === "#") {
      const { canned_responses } = this.props;
      const { show_canned_responses } = this.state;
      if (!show_canned_responses)
        this.showCannedResponses();
      const cn_search_key = plainText.slice(1, plainText.length);
      this.debouncedFilterCannedGroupBySearchKey(cn_search_key.trim(), canned_responses, () => {
        if (cn_search_key.endsWith(" ")) {
          const first_cn = this.getFirstFilterCannedMessage(cn_search_key.trim());
          if (first_cn)
            this.onClickCannedMessage(first_cn);
          else
            this.closeOverlayPopup();
        }
      });
    } else if (plainText.charAt(0) === "@") {
      const tag_search_key = plainText.slice(1, plainText.length);
      this.setState({
        filtered_tags: tag_search_key.length === 0 ? this.props.tags : filterArrayBySearchText(tag_search_key, this.props.tags, "name"),
      })
    }
  }

  handleKeyCommand = e => {
    if (!(e.keyCode === 13 && !e.shiftKey)) {
      const { typing } = this.state;
      if (!typing) {
        this.setState({ typing: true }, () => {
          const { sendTypingIndication } = this.props;
          if (sendTypingIndication)
            sendTypingIndication(true)
        });
        this.typingTimer = setTimeout(this.stopTypingIndicator, 3000);
      } else {
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(this.stopTypingIndicator, 3000);
      }
    }
  }

  renderCannedResponsesPopup = () => {
    const { canned_responses_loading } = this.props
    const { show_canned_responses, selected_node, default_expand_all, filtered_cn_group } = this.state;
    if (show_canned_responses) {
      return (
        <React.Fragment>
          <ShortcutList
            cn_loading={canned_responses_loading}
            cn_group={filtered_cn_group}
            selected_key={selected_node.key}
            default_expand_all={default_expand_all}
            onSelectTreeNode={this.onSelectTreeNode}
          />
          {
            selected_node.messages &&
            <div
              className="ori-absolute ori-align-bottom ori-align-left-full ori-lr-pad-5 ori-flex ori-zindex-1"
              style={{ width: "400px" }}
            >
              <div className="ori-animated ori-fade-in ori-bg-white ori-box-shadow ori-lr-pad-5">
                {
                  selected_node.messages.map((msg, index) => {
                    return (
                      <div
                        key={index}
                        className="ori-lr-pad-5 ori-tb-mrgn-5 ori-font-13 ori-bg-default-hover-primary-light ori-font-default-hover-primary"
                        onClick={this.onClickCannedMessage.bind(this, { message: msg, id: selected_node.id })}
                      >
                        {msg}
                      </div>
                    );
                  })
                }
              </div>
            </div>
          }
        </React.Fragment>
      );
    }
  };

  renderTagsPopup = () => {
    const { show_tags_popup, filtered_tags } = this.state;
    const { tags_loading } = this.props;
    if (show_tags_popup) {
      return (
        <div
          className="ori-animated ori-fade-in ori-box-shadow ori-bg-white ori-overflow-y-auto"
          style={{
            maxHeight: "460px",
            maxWidth: "300px"
          }}
        >
          {
            tags_loading ?
              <SpinnerLoader size="small" text="loading" /> :
              (filtered_tags.length !== 0 ?
                filtered_tags.map(tag => (
                  <div
                    key={tag._id}
                    className="ori-lr-pad-10 ori-tb-pad-5 ori-cursor-ptr ori-font-default-hover-primary ori-bg-white-hover-primary-light"
                    onClick={this.onSelectTag.bind(this, tag)}
                  >
                    {tag.name}
                  </div>
                ))
                :
                <div className="ori-pad-10 ori-font-warning ori-font-xs">Tag list is empty.</div>)
          }
        </div>
      );
    }
  };

  renderTooltipTitle = (title, shortcut) => {
    return (
      <React.Fragment>
        <p className="ori-font-sm ori-text-center ori-b-mrgn-10">{title}</p>
        <p className="ori-font-xs ori-text-center ori-b-mrgn-10">
          <span className="ori-tb-pad-3 ori-lr-pad-5 ori-lr-mrgn-5 ori-border-radius-4 ori-bg-default">Ctrl</span>+
          <span className="ori-tb-pad-3 ori-lr-pad-5 ori-lr-mrgn-5 ori-border-radius-4 ori-bg-default">{shortcut}</span>
        </p>
        <p className="ori-font-xs ori-text-center ori-b-mrgn-10">
          <span className="ori-tb-pad-3 ori-lr-pad-5 ori-lr-mrgn-5 ori-border-radius-4 ori-bg-default">Cmd</span>+
          <span className="ori-tb-pad-3 ori-lr-pad-5 ori-lr-mrgn-5 ori-border-radius-4 ori-bg-default">{shortcut}</span>
        </p>
      </React.Fragment>
    )
  }

  render() {
    const { composer_type, show_canned_responses, show_tags_popup, editorHtml } = this.state;
    const { show_composer_type } = this.props;
    return (
      <div className="ori-relative ori-animated ori-fade-in ori-box-shadow ori-flex-column ori-flex-jc chatComposerContainer">
        {
          (show_canned_responses || show_tags_popup) &&
          <div
            className="ori-absolute ori-align-left ori-align-bottom-full ori-b-pad-5"
            ref={this.popupOverlayRef}
          >
            <div className="ori-relative">
              {this.renderCannedResponsesPopup()}
              {this.renderTagsPopup()}
            </div>
          </div>
        }
        <div className="ori-absolute ori-flex-row alignIconContainer">
          <Tooltip
            overlayClassName="ori-tooltip-tag ori-tooltip-bg-white ori-tooltip-arrow-hidden"
            title="Canned Responses"
            mouseEnterDelay={1.5}
          >
            <div
              className={`ori-box-24 ori-zindex-1 ori-border-radius-4 ori-flex-row ori-flex-center ori-cursor-ptr ori-lr-mrgn-3 ${show_canned_responses ? "ori-bg-primary ori-border-primary ori-font-white" : "ori-font-default ori-bg-default ori-border-light"}`}
              onClick={this.showCannedResponses}
            >
              <span className="ori-font-sm">#</span>
            </div>
          </Tooltip>
          <Tooltip
            overlayClassName="ori-tooltip-tag ori-tooltip-bg-white ori-tooltip-arrow-hidden"
            title="Tags"
            mouseEnterDelay={1.5}
          >
            <div
              className={`ori-box-24 ori-zindex-1 ori-border-radius-4 ori-flex-row ori-flex-center ori-cursor-ptr ori-lr-mrgn-3 ${show_tags_popup ? "ori-bg-primary ori-border-primary ori-font-white" : "ori-font-default ori-bg-default ori-border-light"}`}
              onClick={this.showTagsPopup}
            >
              <Icon type="tag" />
            </div>
          </Tooltip>
          <div
            id="toolbar"
            className="ori-flex-row"
          >
            {
              MODULE_CONFIG.CHATS.EDITOR.BOLD &&
              <Tooltip
                overlayClassName="ori-tooltip-tag ori-tooltip-bg-white ori-tooltip-arrow-hidden"
                title={this.renderTooltipTitle("Bold", "B")}
                mouseEnterDelay={1.5}
              >
                <button className="ori-box-24 ori-no-pad ori-border-radius-4 ori-lr-mrgn-3 ori-zindex-1 ori-border-light ori-font-default ori-bg-default ql-bold">
                  <Icon type="bold" />
                </button>
              </Tooltip>
            }
            {
              MODULE_CONFIG.CHATS.EDITOR.ITALIC &&
              <Tooltip
                overlayClassName="ori-tooltip-tag ori-tooltip-bg-white ori-tooltip-arrow-hidden"
                title={this.renderTooltipTitle("Italic", "I")}
                mouseEnterDelay={1.5}
              >
                <button className="ori-box-24 ori-no-pad ori-border-radius-4 ori-lr-mrgn-3 ori-zindex-1 ori-border-light ori-font-default ori-bg-default ql-italic">
                  <Icon type="italic" />
                </button>
              </Tooltip>
            }
            {
              MODULE_CONFIG.CHATS.EDITOR.UNDERLINE &&
              <Tooltip
                overlayClassName="ori-tooltip-tag ori-tooltip-bg-white ori-tooltip-arrow-hidden"
                title={this.renderTooltipTitle("Underline", "U")}
                mouseEnterDelay={1.5}
              >
                <button className="ori-box-24 ori-no-pad ori-border-radius-4 ori-lr-mrgn-3 ori-zindex-1 ori-border-light ori-font-default ori-bg-default ql-underline">
                  <Icon type="underline" />
                </button>
              </Tooltip>
            }
            {
              MODULE_CONFIG.CHATS.EDITOR.STRIKE &&
              <Tooltip
                overlayClassName="ori-tooltip-tag ori-tooltip-bg-white ori-tooltip-arrow-hidden"
                title="Strikethrough"
                mouseEnterDelay={1.5}
              >
                <button className="ori-box-24 ori-no-pad ori-border-radius-4 ori-lr-mrgn-3 ori-zindex-1 ori-border-light ori-font-default ori-bg-default ql-strike">
                  <Icon type="strikethrough" />
                </button>
              </Tooltip>
            }
          </div>
          {
            show_composer_type &&
            <div className="ori-lr-mrgn-3">
              <Radio.Group
                size="small"
                buttonStyle="solid"
                value={composer_type}
                onChange={this.onChangeComposerType}
              >
                <Radio.Button
                  className="ori-font-normal ori-font-xs"
                  value={INPUT_COMPOSER_TYPE.WHISPER}
                >
                  Whisper
                  </Radio.Button>
                <Radio.Button
                  className="ori-font-normal ori-font-xs"
                  value={INPUT_COMPOSER_TYPE.BARGE}
                >
                  Barge
                </Radio.Button>
              </Radio.Group>
            </div>
          }
        </div>
        <div className="editorContainer">
          <ReactQuill
            ref={this.editorRef}
            theme={null}
            className="quillEditorContainer"
            placeholder="Type your Message..."
            value={editorHtml}
            onChange={this.onChangeEditor}
            onKeyDown={this.handleKeyCommand}
            formats={MODULE_CONFIG.CHATS.EDITOR.FORMATS}
            modules={this.editorModules}
          />
        </div>
        <Button
          type="primary"
          className="ori-absolute ori-no-pad ori-border-radius-4 ori-flex-row ori-flex-center alignSendBtn"
          disabled={!editorHtml}
          onClick={this.sendHtmlContent}
        >
          <PaperPlane size={28} />
        </Button>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    canned_responses: state.member_details.canned_responses,
    canned_responses_loading: state.member_details.loaders.canned_responses_loading,
    admin_email: state.admin_details.user_info ? state.admin_details.user_info.email : null,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {
        getAgentCannedResponses
      },
      dispatch),
  };
};

ChatComposer.propTypes = {
  handleMessageSend: PropTypes.func,
  show_composer_type: PropTypes.bool,
  canned_responses: PropTypes.array,
  sendTypingIndication: PropTypes.func,
  tags: PropTypes.array,
  tags_loading: PropTypes.bool,
  updateSelectedTag: PropTypes.func,
  active_chat_item: PropTypes.object,
  actions: PropTypes.object,
  admin_email: PropTypes.string,
  canned_responses_loading: PropTypes.bool
};

ChatComposer.defaultProps = {
  handleMessageSend: () => { },
  show_composer_type: false,
  tags: [],
  tags_loading: false,
  active_chat_item: null
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatComposer);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/components/chatinterface/components/chatcomposer/index.js